import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useMemo, useState } from "react";
import { getLinkTitle, getLinkImageUrl } from "@shared/blocks/link/helpers";
import "./view.scss";
function stopCardEvent(e) {
    e.stopPropagation();
}
function extractVideoId(content) {
    if (content.subtype === "youtube" && content.youtube.videoId) {
        return content.youtube.videoId;
    }
    const url = content.url;
    if (!url)
        return undefined;
    try {
        const parsed = new URL(url);
        if (parsed.hostname === "youtu.be") {
            return parsed.pathname.slice(1).split("/")[0] || undefined;
        }
        if (parsed.hostname.includes("youtube.com")) {
            const vParam = parsed.searchParams.get("v");
            if (vParam)
                return vParam;
            const segments = parsed.pathname.split("/").filter(Boolean);
            if (segments[0] === "embed" || segments[0] === "shorts") {
                return segments[1] || undefined;
            }
        }
    }
    catch {
        // malformed URL
    }
    return undefined;
}
export const YouTubeBlockView = memo(({ block, blockParams }) => {
    const content = block.content;
    const videoId = extractVideoId(content);
    const isExpanded = blockParams?.isExpanded === true;
    const [thumbnailIndex, setThumbnailIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    if (!videoId) {
        return (_jsx("div", { className: "youtube-content youtube-content--fallback", children: _jsx("a", { href: content.url, target: "_blank", rel: "noopener noreferrer", className: "youtube-content__link", onClick: stopCardEvent, onMouseDown: stopCardEvent, children: getLinkTitle(content) || "YouTube" }) }));
    }
    const contentImageUrl = getLinkImageUrl(content);
    const thumbnailCandidates = useMemo(() => {
        const fromContent = contentImageUrl?.trim();
        const candidates = [
            fromContent,
            `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        ].filter((url) => Boolean(url));
        return Array.from(new Set(candidates));
    }, [contentImageUrl, videoId]);
    const thumbnailUrl = thumbnailCandidates[thumbnailIndex] ?? thumbnailCandidates[0];
    const titleText = getLinkTitle(content)?.trim() || block.title || "YouTube";
    const youtubePayload = content.subtype === "youtube" ? content.youtube : undefined;
    const channelLabel = youtubePayload?.channelTitle?.trim() || "YouTube";
    const channelInitial = channelLabel.charAt(0).toUpperCase() || "Y";
    const rootClassName = `youtube-content${isExpanded ? " youtube-content--expanded" : ""}`;
    if (isExpanded && isPlaying) {
        return (_jsx("div", { className: rootClassName, children: _jsx("div", { className: "youtube-content__player", children: _jsx("iframe", { className: "youtube-content__iframe", src: `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`, title: titleText, allow: "autoplay; encrypted-media; picture-in-picture", allowFullScreen: true, onMouseDown: stopCardEvent, onClick: stopCardEvent }) }) }));
    }
    const facadeBody = (_jsxs(_Fragment, { children: [thumbnailUrl ? (_jsx("img", { className: "youtube-content__thumbnail", src: thumbnailUrl, alt: titleText, loading: "lazy", onError: () => {
                    setThumbnailIndex((currentIndex) => {
                        const nextIndex = currentIndex + 1;
                        if (nextIndex >= thumbnailCandidates.length)
                            return currentIndex;
                        return nextIndex;
                    });
                } })) : (_jsx("div", { className: "youtube-content__thumbnail youtube-content__thumbnail--fallback" })), _jsx("span", { className: "youtube-content__play-trigger", "aria-hidden": true, children: _jsx("span", { className: "youtube-content__play-icon", children: _jsx("span", { className: "youtube-content__play-arrow" }) }) }), _jsx("div", { className: "youtube-content__title-overlay", children: _jsxs("div", { className: "youtube-content__title-row", children: [_jsx("span", { className: "youtube-content__channel-avatar youtube-content__channel-avatar--fallback", "aria-hidden": true, children: channelInitial }), _jsx("div", { className: "youtube-content__title", "aria-label": titleText, children: titleText })] }) })] }));
    return (_jsx("div", { className: rootClassName, children: _jsx("div", { className: "youtube-content__player", children: isExpanded ? (_jsx("button", { type: "button", className: "youtube-content__facade", onClick: (e) => {
                    e.stopPropagation();
                    setIsPlaying(true);
                }, onMouseDown: stopCardEvent, "aria-label": "Play video", children: facadeBody })) : (_jsx("div", { className: "youtube-content__facade youtube-content__facade--passive", children: facadeBody })) }) }));
});
